import React from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyPage } from '@quality24/inpatient-typings';
import {
  Heading,
  ScaleSelection,
  Text,
  TextArea,
} from '@quality24/design-system';
import classNames from 'classnames';
import { SurveyHook } from '@/inpatient-patient-pwa/hooks/useSurvey';
import ImprovementsSelector from '@/inpatient-patient-pwa/molecules/ImprovementsSelector';
import useImprovements from '../useImprovements';
import styles from './DefaultQuestionPage.module.scss';

export interface Props {
  page: Pick<
    SurveyPage,
    'id' | 'category' | 'type' | 'titleI18N' | 'descriptionI18N' | 'data'
  >;
  answer: SurveyHook['answer'];
}

const DefaultQuestionPage: React.FunctionComponent<Props> = ({
  page,
  answer,
}) => {
  const { t } = useTranslation();
  const description = page.descriptionI18N;
  const improvements = useImprovements(page.data);

  const show =
    answer.values.value.score !== undefined && answer.values.value.score < 10;

  return (
    <>
      <Heading as="h1" weight="semiBold" size="lg1">
        {page.titleI18N}
      </Heading>
      <Text className="my-4" style={{ whiteSpace: 'pre-wrap' }}>
        {description}
      </Text>

      <ScaleSelection
        className={classNames(styles['scale-selection'], 'mb-5')}
        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
        colors={[
          'nps1',
          'nps2',
          'nps3',
          'nps4',
          'nps5',
          'nps6',
          'nps7',
          'nps8',
          'nps9',
          'nps10',
        ]}
        compact
        value={answer.values.value.score}
        onChange={(v) => {
          answer.setFieldValue('value.score', v);
          if (v === 10) {
            answer.setFieldValue('value.improvements', undefined);
          }
        }}
      />
      {show && improvements.titles.length > 0 && (
        <ImprovementsSelector
          className="my-4"
          titles={improvements.titles}
          value={improvements.toDisplayed(
            answer.values.value.improvements || [],
          )}
          onChange={(v: string[]) =>
            answer.setFieldValue('value.improvements', improvements.toStored(v))
          }
        />
      )}
      <TextArea
        id="formFeedback"
        className="my-4"
        label={t('survey.comment.label')}
        name="text"
        value={answer.values.value.text}
        errorText={answer.errors?.value?.text}
        onChange={(e) => answer.setFieldValue('value.text', e.target.value)}
      />
    </>
  );
};

export default DefaultQuestionPage;
